import React from 'react';
import { Link } from 'react-router-dom';

const GRID_BG = {
  backgroundImage:
    'linear-gradient(rgba(201,168,76,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.12) 1px, transparent 1px)',
  backgroundSize: '60px 60px',
};

const PillarCard = ({ number, title, bullets }) => (
  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:border-gold-200 hover:shadow-md transition-all">
    <div className="flex items-center gap-3 mb-5">
      <span className="w-8 h-8 bg-gold-500 rounded-lg flex items-center justify-center text-navy-900 font-black text-sm shrink-0">
        {number}
      </span>
      <h3 className="text-base font-bold text-navy-800 font-heading leading-snug">{title}</h3>
    </div>
    <ul className="space-y-2.5">
      {bullets.map((b, i) => (
        <li key={i} className="flex items-start gap-2.5 text-slate-600 text-sm">
          <span className="text-gold-500 mt-0.5 shrink-0 font-bold">›</span>
          {b}
        </li>
      ))}
    </ul>
  </div>
);

const PILLARS = [
  {
    number: '1',
    title: 'Thought Leadership & C-Suite Advisory',
    bullets: [
      'Forward-looking advisory anticipating regulatory shifts — AI governance, supply chain mandates, and quantum deadlines — before they impact operations',
      'Board-level translation of cyber, cryptographic, and risk telemetry into clear, data-driven business intelligence',
      'Governance model development, corporate ethics frameworks, and risk-tolerance parameters aligned to growth goals',
    ],
  },
  {
    number: '2',
    title: 'SOC 2 & Pre-Audit Preparation',
    bullets: [
      'SOC 2 Type I & II readiness — designing, implementing, and testing controls across Security, Availability, Processing Integrity, Confidentiality, and Privacy',
      'Simulated pre-audit assessments against SOC 2, NIST SP 800-171 / CMMC, and ISO/IEC 27001',
      'Evidence packaging that reduces assessment friction and accelerates time-to-certification',
    ],
  },
  {
    number: '3',
    title: 'Continuous Auditing & Real-Time Compliance',
    bullets: [
      'Automated evidence collection continuously gathering and validating compliance artifacts across cloud and on-premise environments',
      'Continuous Control Monitoring (CCM) to surface control failures, data drifts, and anomalies the moment they occur',
      'Elimination of the chaotic, manual audit rush through always-on compliance readiness',
    ],
  },
  {
    number: '4',
    title: 'Proactive Reviews & Strategic Health Checks',
    bullets: [
      'Preemptive gap analysis across technical environments, access management controls, and physical security standards',
      'Policy and process drift audits ensuring daily operations match written security policies — catching issues before they trigger audit failures',
      'Architectural red-teaming of cloud, hybrid, and on-premise systems for single points of failure and compliance blind spots',
    ],
  },
  {
    number: '5',
    title: 'Post-Quantum Cryptography (PQC) Readiness',
    bullets: [
      'Cryptographic asset inventories mapping all legacy RSA/ECC exposure across networks, applications, and firmware',
      'Alignment to finalized NIST PQC standards: FIPS 203 (ML-KEM), FIPS 204 (ML-DSA), and FIPS 205 (SLH-DSA)',
      'Phased PQC migration roadmaps aligned to federal deadlines and mandatory procurement attestations — surviving "Q-Day"',
    ],
  },
  {
    number: '6',
    title: 'AI Governance & Risk Management',
    bullets: [
      'AI lifecycle alignment with the NIST AI Risk Management Framework (AI RMF) and evolving international standards',
      'Model integrity assessment covering data misuse, algorithmic bias, shadow AI usage, and adversarial prompt vulnerabilities',
    ],
  },
  {
    number: '7',
    title: 'POA&M Management & Remediation',
    bullets: [
      'POA&M strategy and tracking with precise, realistic remediation steps, resource requirements, and milestones that satisfy regulatory authorities',
      'Root-cause remediation engineering permanent technical and operational solutions — moving beyond temporary fixes',
    ],
  },
];

const FRAMEWORKS = [
  'SOC 2 Type I & II', 'FISMA', 'FISCAM', 'SSAE 18', 'NIST 800-53',
  'NIST 800-171', 'CMMC', 'ISO/IEC 27001', 'NIST AI RMF',
  'FIPS 203', 'FIPS 204', 'FIPS 205', 'NSM-10', 'COBIT', 'COSO',
];

const WHY = [
  {
    title: 'Battle-Tested Experts',
    desc: '30+ years in audit and audit support. We know exactly how auditors think, what they look for, and how to defend your controls.',
  },
  {
    title: 'C-Suite Visionaries',
    desc: 'We deliver strategic clarity and executive-level thought leadership — not just compliance checklists.',
  },
  {
    title: 'Proactive, Not Reactive',
    desc: 'Continuous readiness, proactive reviews, and automated compliance move your organization from fire-fighting to predictable operational calm.',
  },
  {
    title: 'Federal & GovCon Alignment',
    desc: 'Deep expertise in federal procurement prerequisites — including mandatory PQC transitions and continuous monitoring — ensuring contract eligibility.',
  },
];

const GRC = () => {
  return (
    <div className="pt-20">

      {/* Page header */}
      <section className="relative bg-navy-800 py-20 overflow-hidden">
        <div className="absolute inset-0" style={GRID_BG} />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gold-500/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-6">
            <Link to="/" className="hover:text-gold-400 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-gold-400">Governance, Risk & Compliance</span>
          </nav>
          <p className="text-gold-500 text-sm font-bold uppercase tracking-widest mb-4">Service Line</p>
          <h1 className="text-4xl md:text-5xl font-black text-white font-heading leading-tight max-w-3xl">
            Governance, Risk &amp; Compliance
          </h1>
          <p className="text-slate-300 text-lg mt-6 max-w-2xl leading-relaxed">
            Managing compliance is no longer just an operational requirement — it is a competitive advantage.
            DTX combines 30+ years of audit expertise with AI-driven GRC methodologies, continuous compliance
            automation, and Post-Quantum Cryptography readiness to transform regulatory burden into strategic clarity.
          </p>
        </div>
      </section>

      {/* Pillars grid */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-gold-600 text-sm font-bold uppercase tracking-widest mb-4">Our Capabilities</p>
            <h2 className="text-4xl font-black text-navy-800 font-heading">Core GRC Pillars</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {PILLARS.map((p) => (
              <PillarCard key={p.number} {...p} />
            ))}
          </div>
        </div>
      </section>

      {/* Frameworks strip */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-gold-600 text-sm font-bold uppercase tracking-widest mb-4">Standards & Frameworks</p>
            <h2 className="text-3xl font-black text-navy-800 font-heading">Compliance We Support</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {FRAMEWORKS.map((f) => (
              <span
                key={f}
                className="bg-slate-50 border border-slate-200 text-navy-700 px-4 py-2 rounded-full text-sm font-semibold hover:border-gold-300 hover:bg-gold-50 transition-colors"
              >
                {f}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-gold-600 text-sm font-bold uppercase tracking-widest mb-4">Why DTX</p>
            <h2 className="text-4xl font-black text-navy-800 font-heading">Why Partner with DTX</h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto">
              We bridge the gap between battle-tested compliance principles and the automated,
              rapid-response demands of modern enterprise and federal environments.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map(({ title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 bg-gold-500/10 border border-gold-200 rounded-xl flex items-center justify-center mb-4">
                  <div className="w-3 h-3 bg-gold-500 rounded-full" />
                </div>
                <h3 className="font-bold text-navy-800 font-heading mb-2">{title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-800 py-16 relative overflow-hidden">
        <div className="absolute inset-0" style={GRID_BG} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-black text-white font-heading mb-4">
            Ready to Transform Your Compliance Posture?
          </h2>
          <p className="text-slate-300 mb-8 max-w-lg mx-auto">
            Contact us to discuss your GRC requirements and how DTX can deliver always-on compliance for your agency or enterprise.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-gold-500 hover:bg-gold-400 text-navy-900 px-8 py-4 rounded-xl font-bold transition-colors shadow-lg"
          >
            Get in Touch
          </Link>
        </div>
      </section>

    </div>
  );
};

export default GRC;
